import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import {
  BiBoltCircle,
  BiPhoneCall,
  BiShow,
  BiSolidBot,
  BiSolidBulb,
  BiSolidConversation,
  BiSolidMoviePlay,
  BiSolidPackage,
  BiSolidPhone,
  BiSolidUserPlus,
  BiStar,
} from 'react-icons/bi';
import { BsLightbulb } from 'react-icons/bs';
import { GiLaurelsTrophy } from 'react-icons/gi';

import ProBadge from '@/components/ui/ProBadge';
import usePermission from '@/hooks/usePermission';
import i18n from '@/i18n/config';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import { BotCategoryEnum } from '@/utils/BotCategoryEnum';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

function ChatBotTypeList({
  data,
  selected,
  handleSelectClone,
  handleSelectWidget,
  hasPermission,
}: {
  data: [];
  selected: string | null;
  handleSelectClone: (value: string) => void;
  handleSelectWidget: (value: object) => void;
  hasPermission?: boolean;
}) {
  const { t } = useTranslation();
  const permissions = usePermission();

  const iconsAndText: {
    [key: string]: {
      icon: React.ReactNode;
      title: string;
      description: React.ReactNode;
    };
  } = {
    MCBOT_01: {
      icon: <BiSolidConversation className="tw-text-blue-500" size={25} />,
      title: t('DashboardCreatePartial/key3'),
      description: (
        <div className="tw-mt-2">
          <div className="tw-mt-10">
            <span className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {' '}
              {ReactHtmlParser(t('DashboardCreatePartial/MCBOT_01/key3'))}
              <br /> {ReactHtmlParser(t('DashboardCreatePartial/MCBOT_01/key4'))}
            </span>
          </div>
        </div>
      ),
    },
    VIDEO_SCENARIO: {
      icon: <BiSolidMoviePlay className="tw-text-blue-500" size={25} />,
      title: t('DashboardCreatePartial/key5'),
      description: (
        <div className="tw-mt-2">
          <div className="tw-mt-10">
            <span className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {' '}
              {ReactHtmlParser(t('DashboardCreatePartial/VIDEO_SCENARIO/key2'))}
              <br /> {ReactHtmlParser(t('DashboardCreatePartial/VIDEO_SCENARIO/key3'))}
              <br /> {ReactHtmlParser(t('DashboardCreatePartial/VIDEO_SCENARIO/key4'))}
            </span>
          </div>
        </div>
      ),
    },
    KITTY_02: {
      icon: <BiSolidBot className="tw-text-blue-500" size={25} />,
      title: t('DashboardCreatePartial/key4'),
      description: (
        <div className="tw-mt-2">
          <div className="tw-mt-10">
            <span className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {' '}
              {ReactHtmlParser(t('DashboardCreatePartial/KITTY_02/key2'))}
              <br /> {ReactHtmlParser(t('DashboardCreatePartial/KITTY_02/key3'))}
              <br /> {ReactHtmlParser(t('DashboardCreatePartial/KITTY_02/key4'))}
            </span>
          </div>
        </div>
      ),
    },
    KITTY_03: {
      icon: <BiSolidBulb className="tw-text-blue-500" size={25} />,
      title: t('DashboardCreatePartial/key2'),
      description: (
        <div className="tw-mt-2">
          <div className="tw-mt-10">
            <span className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {' '}
              {ReactHtmlParser(t('DashboardCreatePartial/KITTY_03/key2'))}
              <br /> {ReactHtmlParser(t('DashboardCreatePartial/KITTY_03/key3'))}
              <br /> {ReactHtmlParser(t('DashboardCreatePartial/KITTY_03/key4'))}
            </span>
          </div>
        </div>
      ),
    },
    CLONE_01: {
      icon: <BiSolidUserPlus className="tw-text-blue-500" size={25} />,
      title: t('DashboardCreatePartial/key1'),
      description: null,
    },
    KITTY_04: {
      icon: <BiSolidPackage className="tw-text-blue-500" size={25} />,
      title: t('DashboardCreatePartial/key6'),
      description: (
        <div className="tw-mt-2">
          <div className="tw-mt-10">
            <span className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {' '}
              {ReactHtmlParser(t('DashboardCreatePartial/KITTY_04/key2'))}
              <br />
              {ReactHtmlParser(t('DashboardCreatePartial/KITTY_04/key3'))}
              <br />
              {ReactHtmlParser(t('DashboardCreatePartial/KITTY_04/key4'))}
            </span>
          </div>
        </div>
      ),
    },
    CALLBOT: {
      icon: <BiSolidPhone className="tw-text-blue-500" size={25} />,
      title: t('DashboardCreatePartial/key10'),
      description: (
        <div className="tw-mt-2">
          <div className="tw-mt-10">
            <span className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {' '}
              {ReactHtmlParser(t('DashboardCreatePartial/CALLBOT/key2'))}
              <br /> {ReactHtmlParser(t('DashboardCreatePartial/CALLBOT/key3'))}
              <br /> {ReactHtmlParser(t('DashboardCreatePartial/CALLBOT/key4'))}
            </span>
          </div>
        </div>
      ),
    },
  };

  const videoMapper: { [key: string]: string } = {
    KITTY_02: 'https://cdn.1cdn.app/application/LOUISEBOT/24062408291855_AI-bot_(2).mp4',
    KITTY_03: 'https://cdn.1cdn.app/application/LOUISEBOT/2406240829186_AI-knowledge-bot.mp4',
    KITTY_04: 'https://cdn.1cdn.app/application/LOUISEBOT/24030408595450_Assistant_with_AI.mp4',
    MCBOT_01: 'https://cdn.1cdn.app/application/LOUISEBOT/2406240829184_text-bot.mp4',
    VIDEO_SCENARIO: 'https://cdn.1cdn.app/application/LOUISEBOT/24062408291812_video-bot.mp4',
    CLONE_01: 'https://cdn.1cdn.app/application/LOUISEBOT/24062408291855_AI-bot_(2).mp4',
    CALLBOT: 'https://cdn.1cdn.app/application/LOUISEBOT/25071507124420_callbot_preview.mp4',
  };

  const additionalTags = (code: string) => {
    let tag = null;
    switch (code) {
      case BotTypeEnum.TEXT_SCENARIO:
        tag = (
          <span className="tw-px-3 tw-py-0.5  tw-bg-emerald-100 tw-text-black tw-rounded-2xl tw-justify-center tw-items-center tw-gap-1.5 tw-flex tw-text-xs tw-font-['Lexend']">
            <BiStar size={15} />
            {t('Wizard/step1/includeAI')}
          </span>
        );
        break;
      case BotTypeEnum.VIDEO_SCENARIO:
        tag = (
          <div className="tw-px-3 tw-py-0.5 tw-bg-primary tw-rounded-md tw-justify-center tw-items-center tw-gap-1.5 tw-flex tw-text-white tw-text-xs tw-font-['Lexend']">
            <BiShow size={15} />
            {t('Wizard/step1/badgeEngaging')}
          </div>
        );
        break;
      case BotTypeEnum.AI_READY_TO_USE:
        tag = (
          <div className="tw-px-3 tw-py-0.5 tw-bg-primary tw-rounded-md tw-justify-center tw-items-center tw-gap-1.5 tw-flex tw-text-white tw-text-xs tw-font-['Lexend']">
            <BiBoltCircle size={20} />
            {t('Wizard/step1/badgeAvailability')}
          </div>
        );
        break;
      case BotTypeEnum.CALLBOT:
        tag = (
          <div className="tw-px-3 tw-py-0.5 tw-bg-yellow-400 tw-rounded-md tw-justify-center tw-items-center tw-gap-1.5 tw-flex tw-text-white tw-text-xs tw-font-['Lexend']">
            <GiLaurelsTrophy size={15} />
            {t('Wizard/step1/badgeInnovation')}
          </div>
        );
        break;
      default:
        tag = <div className="tw-mt-5"></div>;
    }
    return tag;
  };

  const hasRequiredPermission = (code: string) => {
    return code == BotTypeEnum.VIDEO_SCENARIO || code == BotTypeEnum.TEXT_SCENARIO ? permissions.widgetPage.main.some(dr => dr.includes(code)) : hasPermission;
  };

  return (
    <div className="tw-grid tw-grid-cols-10 tw-w-full xl:tw-gap-2 tw-gap-4">
      {data.map((vers: any) => {
        return vers.has_templates ? (
          <React.Fragment key={vers.code}>
            {vers.templates?.map((template: any) => (
              <div
                key={template.id}
                className={`xl:tw-col-span-2 sm:tw-col-span-5 tw-col-span-10 tw-flex tw-flex-col ${
                  selected == template.id ? 'tw-border-4 tw-border-blue-500' : 'tw-border-4 tw-border-transparent'
                } ${' tw-inline-flex '} tw-flex-1 tw-relative tw-border-solid tw-rounded-xl`}
                onClick={() => {
                  handleSelectClone(template.id);
                }}
                onKeyDown={() => handleSelectClone(template.id)}
                role="button"
                id={`btn-bot-selector-${vers.code}`}
                aria-label="bot-selector"
                tabIndex={0}
              >
                <div className="tw-self-stretch tw-bg-white tw-rounded-tl-lg tw-rounded-tr-lg tw-h-[147.95px]">
                  {template.image ? (
                    <img src={template.image} className="tw-w-full tw-h-full tw-object-cover tw-rounded-xl tw-aspect-video" alt="template" />
                  ) : (
                    <video playsInline autoPlay loop muted preload="metadata" className="tw-w-full tw-h-full tw-object-cover tw-rounded-lg tw-aspect-video">
                      <source src={`${videoMapper[vers.code]}#t=0.001`} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>
                <div className="tw-self-stretch tw-flex-1 tw-px-6 tw-pt-4 tw-pb-8 tw-bg-white tw-rounded-bl-lg tw-rounded-br-lg tw-flex-col tw-justify-start tw-items-start tw-gap-3 flex">
                  {template.icon && (
                    <div className="tw-self-stretch tw-w-full tw-justify-between tw-items-center tw-inline-flex tw-gap-3 tw-mb-3">
                      <div className="tw-flex tw-items-center">
                        <div className="tw-p-3 tw-bg-blue-100 tw-rounded-[99px] tw-justify-center tw-items-center tw-gap-2.5 tw-flex tw-relative">
                          <div className="tw-w-6 tw-h-6 tw-relative tw-justify-center tw-items-center tw-flex">{template.icon}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
                    <div className="tw-self-stretch tw-w-full tw-justify-between tw-items-center tw-flex tw-flex-wrap tw-gap-3">
                      <div className="tw-self-stretch tw-text-gray-900 text-base font-normal tw-font-['Lexend'] tw-leading-normal">
                        {i18n.language.startsWith('en') ? template.title.en : template.title.fr}
                      </div>
                    </div>
                    <div
                      className={`tw-px-3 tw-py-0.5 ${
                        vers.type === BotCategoryEnum.AI_BASED_CHATBOT ? 'tw-bg-emerald-100' : 'tw-bg-gray-100'
                      }  tw-rounded-xl tw-justify-center tw-items-center tw-gap-1.5 tw-flex`}
                    >
                      <div
                        className={`tw-text-center ${
                          vers.type === BotCategoryEnum.AI_BASED_CHATBOT ? 'tw-text-emerald-800' : 'tw-text-gray-600'
                        } tw-text-sm tw-font-medium tw-font-['Inter'] tw-leading-tight`}
                      >
                        {vers.type === BotCategoryEnum.AI_BASED_CHATBOT ? t('Wizard/step1/key1') : t('Wizard/step1/key2')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </React.Fragment>
        ) : (
          <div
            key={vers.code}
            className={`xl:tw-col-span-2 lg:tw-col-span-3 sm:tw-col-span-5 tw-col-span-10 tw-flex tw-flex-col ${
              !hasRequiredPermission(vers.code) ? 'tw-cursor-not-allowed' : ''
            } ${
              selected === vers.code ? 'tw-border-blue-500  tw-border-[5px]' : 'tw-border-4 tw-border-transparent'
            } ${'tw-inline-flex'} tw-flex-1 tw-relative tw-border-solid tw-rounded-xl`}
            onClick={() => hasRequiredPermission(vers.code) && handleSelectWidget(vers)}
            onKeyDown={() => hasRequiredPermission(vers.code) && handleSelectWidget(vers)}
            role="button"
            id={`btn-bot-selector-${vers.code}`}
            aria-label="bot-selector"
            tabIndex={0}
          >
            {selected !== vers.code ||
              (!hasRequiredPermission(vers.code) && <div className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-bottom-0 tw-bg-gray-200 tw-bg-opacity-50" />)}
            <div className="tw-self-stretch tw-bg-white tw-rounded-tl-lg tw-rounded-tr-lg tw-h-[147.95px]">
              <video playsInline autoPlay loop muted preload="metadata" className="tw-w-full tw-h-full tw-object-cover tw-rounded-lg tw-aspect-video">
                <source src={`${videoMapper[vers.code]}#t=0.001`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="tw-self-stretch tw-flex-1 tw-px-6 tw-pt-4 tw-pb-8 tw-bg-white tw-rounded-bl-lg tw-rounded-br-lg tw-flex-col tw-justify-start tw-items-start tw-gap-3 flex">
              <div className="tw-self-stretch tw-w-full tw-justify-between tw-items-center tw-inline-flex tw-gap-3 tw-mb-3">
                <div className="tw-flex tw-items-center">
                  <div className="tw-p-3 tw-bg-blue-100 tw-rounded-[99px] tw-justify-center tw-items-center tw-gap-2.5 tw-flex tw-relative">
                    <div className="tw-w-6 tw-h-6 tw-relative tw-justify-center tw-items-center tw-flex">{iconsAndText[vers.code]?.icon}</div>
                  </div>
                </div>

                {additionalTags(vers.code)}
              </div>
              {!hasRequiredPermission(vers.code) && (
                <ProBadge
                  hasCreationBotLimitReached={hasPermission === false}
                  className="tw-w-4 tw-h-4 tw-p-0.5 tw-rounded-[20px] tw-shadow tw-justify-center tw-items-center tw-inline-flex tw-absolute tw-right-0 tw-bottom-0"
                />
              )}
              <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
                <div className="tw-self-stretch tw-text-gray-900 text-base font-normal tw-font-['Lexend'] tw-leading-normal">
                  {iconsAndText[vers.code]?.title}
                </div>
              </div>
              {iconsAndText[vers.code]?.description}
            </div>
          </div>
        );
      })}
    </div>
  );
}
export default WrappedErrorBoundary(ChatBotTypeList);
